import { useState } from 'react';

const ContactPage = () => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);

    const form = e.currentTarget;
    const data = new URLSearchParams();
    const elements = form.elements;

    for (let i = 0; i < elements.length; i++) {
      const el = elements[i] as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (el.name) {
        if (el.type === 'checkbox') {
          if ((el as HTMLInputElement).checked) data.append(el.name, el.value);
        } else {
          data.append(el.name, el.value);
        }
      }
    }

    try {
      await fetch('https://readdy.ai/api/form/d75d2jivu4krcbn3bkng', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main>
      {/* HERO */}
      <section className="relative bg-artden-dark py-32 md:py-40 overflow-hidden">
        <div className="absolute inset-0 opacity-5"
          style={{ backgroundImage: 'radial-gradient(circle, #d4693a 1px, transparent 1px)', backgroundSize: '48px 48px' }}
        />
        <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-artden-coral/20 border border-artden-coral/40 text-artden-coral px-4 py-1.5 rounded-full text-xs font-outfit font-semibold tracking-widest uppercase mb-6">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-mail-send-line"></i></span>
            Get In Touch
          </div>
          <h1 className="font-cormorant text-5xl md:text-7xl font-light text-white leading-tight mb-5">
            Let's Plan<br /><em className="font-medium text-artden-coral">Your Experience</em>
          </h1>
          <p className="font-outfit text-white/70 text-lg max-w-2xl mx-auto leading-relaxed">
            Whether you're booking a class, planning a party, or just have a question — we'd love to hear from you.
          </p>
        </div>
      </section>

      {/* CONTACT + FORM */}
      <section className="bg-artden-light py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14">
            {/* Left: Contact Info */}
            <div>
              <p className="font-outfit text-artden-coral text-xs font-semibold tracking-widest2 uppercase mb-4">Contact Art Den Studio</p>
              <h2 className="font-cormorant text-4xl font-medium text-artden-dark mb-6 leading-tight">
                We're Here to Help
              </h2>
              <p className="font-outfit text-artden-muted text-base leading-relaxed mb-8">
                Reach out to discuss classes, party availability, custom events, or anything else. Kelsey and the Art Den Studio team typically respond within one business day.
              </p>
              <div className="space-y-5 mb-10">
                {[
                  { icon: 'ri-phone-line', label: 'Phone', value: '610-340-0314', href: 'tel:6103400314' },
                  { icon: 'ri-mail-line', label: 'Email', value: 'info@artdenstudio.com', href: 'mailto:info@artdenstudio.com' },
                  { icon: 'ri-map-pin-2-line', label: 'Studio Address', value: '37 E Belvidere Street\nNazareth, PA 18064', href: 'https://maps.google.com/?q=37+E+Belvidere+Street+Nazareth+PA+18064' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-artden-coral/10 rounded-lg shrink-0">
                      <i className={`${item.icon} text-artden-coral text-lg`}></i>
                    </div>
                    <div>
                      <p className="font-outfit text-artden-muted text-xs uppercase tracking-widest mb-1">{item.label}</p>
                      <a href={item.href} target={item.href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer"
                        className="font-outfit text-artden-dark text-sm hover:text-artden-coral transition-colors leading-relaxed whitespace-pre-line">
                        {item.value}
                      </a>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-3">
                <a href="https://www.facebook.com/TheArtDenofNazareth/" target="_blank" rel="noopener noreferrer nofollow"
                  className="w-10 h-10 flex items-center justify-center rounded-full border border-artden-dark/15 text-artden-muted hover:text-artden-coral hover:border-artden-coral transition-all">
                  <i className="ri-facebook-fill text-base"></i>
                </a>
                <a href="https://www.instagram.com/theartdenofnazareth/" target="_blank" rel="noopener noreferrer nofollow"
                  className="w-10 h-10 flex items-center justify-center rounded-full border border-artden-dark/15 text-artden-muted hover:text-artden-coral hover:border-artden-coral transition-all">
                  <i className="ri-instagram-line text-base"></i>
                </a>
              </div>
            </div>

            {/* Right: Form */}
            <div className="bg-white rounded-2xl p-7 md:p-8 border border-artden-cream">
              {submitted ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-14 h-14 flex items-center justify-center bg-green-100 rounded-full mb-5">
                    <i className="ri-check-line text-green-600 text-2xl"></i>
                  </div>
                  <h3 className="font-cormorant text-3xl font-semibold text-artden-dark mb-3">Message Sent!</h3>
                  <p className="font-outfit text-artden-muted text-sm leading-relaxed max-w-xs">
                    Thanks for reaching out! We'll get back to you shortly. You can also call us at 610-340-0314.
                  </p>
                </div>
              ) : (
                <>
                  <h3 className="font-cormorant text-2xl font-semibold text-artden-dark mb-1">Send Us a Message</h3>
                  <p className="font-outfit text-artden-muted text-sm mb-6">Class &amp; party inquiries, questions, or just to say hello.</p>
                  <form
                    id="art-den-inquiry-form"
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="space-y-4"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block font-outfit text-xs font-semibold text-artden-muted uppercase tracking-wider mb-1.5">First Name *</label>
                        <input
                          type="text"
                          name="first_name"
                          required
                          placeholder="Jane"
                          className="w-full border border-artden-cream rounded-lg px-4 py-3 text-sm font-outfit text-artden-dark placeholder-artden-cream focus:outline-none focus:border-artden-coral transition-colors bg-artden-light"
                        />
                      </div>
                      <div>
                        <label className="block font-outfit text-xs font-semibold text-artden-muted uppercase tracking-wider mb-1.5">Last Name *</label>
                        <input
                          type="text"
                          name="last_name"
                          required
                          placeholder="Smith"
                          className="w-full border border-artden-cream rounded-lg px-4 py-3 text-sm font-outfit text-artden-dark placeholder-artden-cream focus:outline-none focus:border-artden-coral transition-colors bg-artden-light"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block font-outfit text-xs font-semibold text-artden-muted uppercase tracking-wider mb-1.5">Email *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="jane@email.com"
                        className="w-full border border-artden-cream rounded-lg px-4 py-3 text-sm font-outfit text-artden-dark placeholder-artden-cream focus:outline-none focus:border-artden-coral transition-colors bg-artden-light"
                      />
                    </div>
                    <div>
                      <label className="block font-outfit text-xs font-semibold text-artden-muted uppercase tracking-wider mb-1.5">Phone</label>
                      <input
                        type="tel"
                        name="phone"
                        placeholder="(610) 000-0000"
                        className="w-full border border-artden-cream rounded-lg px-4 py-3 text-sm font-outfit text-artden-dark placeholder-artden-cream focus:outline-none focus:border-artden-coral transition-colors bg-artden-light"
                      />
                    </div>
                    <div>
                      <label className="block font-outfit text-xs font-semibold text-artden-muted uppercase tracking-wider mb-1.5">I'm Interested In *</label>
                      <select
                        name="interest_type"
                        required
                        className="w-full border border-artden-cream rounded-lg px-4 py-3 text-sm font-outfit text-artden-dark focus:outline-none focus:border-artden-coral transition-colors bg-artden-light cursor-pointer"
                      >
                        <option value="">Select an option...</option>
                        <option value="Art Class (Adult)">Art Class (Adult)</option>
                        <option value="Art Class (Kids)">Art Class (Kids)</option>
                        <option value="Birthday Party">Birthday Party</option>
                        <option value="Bachelorette Party">Bachelorette Party</option>
                        <option value="Corporate / Team Event">Corporate / Team Event</option>
                        <option value="Private Event">Private Event</option>
                        <option value="Gift Card">Gift Card</option>
                        <option value="General Question">General Question</option>
                      </select>
                    </div>
                    <div>
                      <label className="block font-outfit text-xs font-semibold text-artden-muted uppercase tracking-wider mb-1.5">Group Size</label>
                      <select
                        name="group_size"
                        className="w-full border border-artden-cream rounded-lg px-4 py-3 text-sm font-outfit text-artden-dark focus:outline-none focus:border-artden-coral transition-colors bg-artden-light cursor-pointer"
                      >
                        <option value="">Select group size...</option>
                        <option value="Just Me (1)">Just Me (1)</option>
                        <option value="2-5 People">2-5 People</option>
                        <option value="6-10 People">6-10 People</option>
                        <option value="11-15 People">11-15 People</option>
                        <option value="16+ People">16+ People</option>
                      </select>
                    </div>
                    <div>
                      <label className="block font-outfit text-xs font-semibold text-artden-muted uppercase tracking-wider mb-1.5">Message</label>
                      <textarea
                        name="message"
                        rows={4}
                        maxLength={500}
                        placeholder="Tell us about your event, preferred dates, or any questions..."
                        className="w-full border border-artden-cream rounded-lg px-4 py-3 text-sm font-outfit text-artden-dark placeholder-artden-cream focus:outline-none focus:border-artden-coral transition-colors bg-artden-light resize-none"
                      />
                      <p className="font-outfit text-artden-muted text-xs mt-1">Max 500 characters</p>
                    </div>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-artden-coral hover:bg-artden-coral/90 disabled:opacity-60 text-white font-semibold py-4 rounded-full transition-all text-sm whitespace-nowrap cursor-pointer"
                    >
                      {submitting ? 'Sending...' : 'Send Message'}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
